import React from 'react';
import { Box, Card, CardContent, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';

const buildStatusColor = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'default';
  if (normalized.includes('active') || normalized.includes('live')) return 'success';
  if (normalized.includes('pending')) return 'warning';
  if (normalized.includes('inactive') || normalized.includes('cancel')) return 'default';
  return 'info';
};

const buildCardSurface = (statusColor) => {
  if (statusColor === 'success') {
    return {
      borderColor: 'success.light',
      background: 'linear-gradient(145deg, rgba(46,125,50,0.14), rgba(255,255,255,0) 60%)',
    };
  }
  if (statusColor === 'warning') {
    return {
      borderColor: 'warning.light',
      background: 'linear-gradient(145deg, rgba(237,108,2,0.14), rgba(255,255,255,0) 60%)',
    };
  }
  if (statusColor === 'info') {
    return {
      borderColor: 'info.light',
      background: 'linear-gradient(145deg, rgba(2,136,209,0.14), rgba(255,255,255,0) 60%)',
    };
  }
  return {
    borderColor: 'divider',
    background: 'linear-gradient(145deg, rgba(117,117,117,0.12), rgba(255,255,255,0) 60%)',
  };
};

const buildSalesforceUrl = (accountId) => {
  const trimmed = String(accountId || '').trim();
  if (!trimmed) return '';
  return `https://cars-commerce.lightning.force.com/${encodeURIComponent(trimmed)}`;
};

const buildCreditRequestUrl = (accountId) => {
  const trimmed = String(accountId || '').trim();
  if (!trimmed) return '';
  return `https://cars-commerce.lightning.force.com/lightning/r/Account/${encodeURIComponent(trimmed)}/related/Requests__r/view?overrideNavRules=true`;
};

const normalizeWebsiteUrl = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const AppSheetAccountCardsRow = ({
  accounts = [],
  selectedAccountId = null,
  onSelectAccount,
}) => {
  const isAllSelected = !selectedAccountId;

  return (
    <Box sx={{ display: 'flex', overflowX: 'auto', pb: 1 }}>
      <Stack direction="row" spacing={2} sx={{ minWidth: 'max-content' }}>
        <Card
          variant={isAllSelected ? 'outlined' : 'elevation'}
          onClick={() => onSelectAccount?.(null)}
          data-testid="detail-panel-account-card-all"
          sx={{
            minWidth: 220,
            cursor: 'pointer',
            borderColor: isAllSelected ? 'primary.main' : 'divider',
          }}
        >
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              All accounts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {accounts.length} account{accounts.length === 1 ? '' : 's'}
            </Typography>
          </CardContent>
        </Card>

        {accounts.map((account) => {
          const isSelected = account.id && selectedAccountId === account.id;
          const salesforceUrl = buildSalesforceUrl(account.id);
          const creditRequestUrl = buildCreditRequestUrl(account.id);
          const websiteUrl = normalizeWebsiteUrl(account.websiteUrl);
          const brands = Array.isArray(account.brands) ? account.brands : [];
          return (
            <Card
              key={account.id || account.name}
              variant={isSelected ? 'outlined' : 'elevation'}
              onClick={() => onSelectAccount?.(account.id || null)}
              data-testid={`detail-panel-account-card-${account.id || account.name || 'unknown'}`}
              sx={{
                minWidth: 260,
                cursor: 'pointer',
                borderColor: isSelected ? 'primary.main' : buildCardSurface(buildStatusColor(account.status)).borderColor,
                borderWidth: isSelected ? 2 : 1,
                background: isSelected ? 'none' : buildCardSurface(buildStatusColor(account.status)).background,
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {account.name || 'Unnamed account'}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Open Account in Salesforce">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Open Account in Salesforce"
                        disabled={!salesforceUrl}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!salesforceUrl) return;
                          window.open(salesforceUrl, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        <CloudIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Open Website Url">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Open Website Url"
                        disabled={!websiteUrl}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!websiteUrl) return;
                          window.open(websiteUrl, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Open Credit Requests in Salesforce">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Open Credit Requests in Salesforce"
                        disabled={!creditRequestUrl}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!creditRequestUrl) return;
                          window.open(creditRequestUrl, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        <RequestQuoteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                {brands.length ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {brands.slice(0, 3).map((brand) => (
                      <Chip key={brand} size="small" label={brand} variant="outlined" />
                    ))}
                    {brands.length > 3 ? (
                      <Chip size="small" label={`+${brands.length - 3}`} variant="outlined" />
                    ) : null}
                  </Stack>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default AppSheetAccountCardsRow;
